import { Card, Typography, Theme, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { NextSeo } from 'next-seo'
import React from 'react'
import { GetServerSideProps } from 'next'
import Link from 'next/link'
import ReactCountryFlag from 'react-country-flag'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

import Background from '../src/components/Subnav/Background'
import Description from '../src/components/Subnav/Description'
import { getRegions } from '../src/services/regions'
import { urls, tabs } from '../src/utils/config'
import { Region as IRegion } from '../types/region'
import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '950px',
    margin: 'auto',
  },
  container: {
    marginTop: '100px',
    [theme.breakpoints.down('md')]: {
      padding: '20px',
    },
  },
  subContainer: {
    margin: '50px 0',
  },
  typesContainer: {},
  yellowTitleContainer: {
    margin: '20px 0',
    left: '-10px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      fontSize: '16px',
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  yellowTitleBackground: {
    position: 'absolute',
    top: '10px',
    left: '-5px',
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    zIndex: -1,
  },
  yellowTitle: {
    color: theme.palette.primary.main,
    fontSize: '20px',
    marginBottom: '50px',
  },
  prosAndCons: {
    display: 'flex',
    flexDirection: 'row',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    margin: '20px 0',
    minHeight: '50px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  numberRow: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: '50px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  rightSection: {
    maxWidth: '80%',
  },
  numberTitle: {
    fontSize: '48px',
    color: theme.palette.primary.main,
    marginRight: '20px',
  },
  numberSubtitle: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  pros: {
    width: '50%',
    [theme.breakpoints.down('md')]: {
      marginRight: '10px',
    },
  },
  cons: {
    width: '50%',
    [theme.breakpoints.down('md')]: {
      marginLeft: '10px',
    },
  },
  card: {
    padding: '20px',
    border: `2px solid ${theme.palette.primary.main}`,
    width: '350px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: '80px',
    marginBottom: '20px',
    [theme.breakpoints.down('md')]: {
      marginRight: '0',
    },
  },
  bigCard: {
    height: '350px',
  },
  smallCard: {
    height: '250px',
  },
  smallIcon: {
    marginRight: '20px',
    height: 'auto',
    minWidth: '20px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '20px',
    },
  },
  bigIcon: {
    height: '45px',
    width: 'auto',
    margin: '20px 0',
    objectFit: 'contain',
  },
  divider: {
    backgroundColor: theme.palette.secondary.main,
    height: '3px',
    margin: '20px 0',
    width: '100%',
  },
  purpleTitle: {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '22px',
    color: theme.palette.primary.main,
  },
  cardTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
  },
  cardNumber: {
    fontSize: '48px',
    color: theme.palette.primary.main,
    marginRight: '20px',
  },
  title: {
    fontSize: '20px',
    color: theme.palette.text.secondary,
    margin: '40px 0',
  },
  mainTitle: {
    fontSize: '22px',
    color: theme.palette.primary.main,
    margin: '40px 0',
  },
  regionName: {
    fontSize: '20px',
    color: theme.palette.primary.main,
    marginLeft: '20px',
  },
  regionCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '300px',
    padding: '10px 20px',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '0',
    boxShadow: 'none',
    '&:hover': {
      cursor: 'pointer',
      opacity: 0.8,
    },
  },
  link: {
    margin: '20px 0',
    '& h6': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      fontSize: '20px',
      fontWeight: 'normal',
      '&:hover': {
        cursor: 'pointer',
        opacity: '0.8',
      },
    },
  },
}))

interface Props {
  regions: IRegion[]
}

function BuyBitcoin({ regions }: Props): JSX.Element {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.buyBitcoin,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`How to buy bitcoin easily with Credit card, Bank or PayPal`}
            description={`In this guide we show you the easiest ways to buy bitcoin in your country using your preferred payment methods. We compiled a list of the the best bitcoin exchanges and wallets for you.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.buyBitcoin}`,
              title: `How to buy bitcoin easily with Credit card, Bank or PayPal`,
              description: `In this guide we show you the easiest ways to buy bitcoin in your country using your preferred payment methods. We compiled a list of the the best bitcoin exchanges and wallets for you.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <Background />
            <Description selectedTab={tabs.buyBitcoin} />
            <div className={classes.container}>
              <div className={classes.typesContainer}>
                <div className={classes.yellowTitleContainer}>
                  <div className={classes.yellowTitleBackground} />
                  <Typography variant="subtitle1" className={classes.yellowTitle} id="read_more">
                    How to buy Bitcoin
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Card className={`${classes.card} ${classes.bigCard}`}>
                    <div className={classes.cardTitle}>
                      <Typography variant="subtitle1" className={classes.cardNumber}>
                        01
                      </Typography>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Register for free
                      </Typography>
                    </div>
                    <Divider className={classes.divider} />
                    <img width={45} height={45} src={`/svg/file.svg`} className={classes.bigIcon} />
                    <Typography variant="body1">
                      Sign-up for an exchange that suits your needs. All you need is an email
                      address and password.
                    </Typography>
                  </Card>
                  <Card className={`${classes.card} ${classes.bigCard}`}>
                    <div className={classes.cardTitle}>
                      <Typography variant="subtitle1" className={classes.cardNumber}>
                        02
                      </Typography>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Verify your identity
                      </Typography>
                    </div>
                    <Divider className={classes.divider} />
                    <img
                      width={45}
                      height={45}
                      src={`/svg/fingerprint.svg`}
                      className={classes.bigIcon}
                    />
                    <Typography variant="body1">
                      To buy bitcoin with fiat money you’ll need to upload a photo of your ID.
                      Exchanges are required to do this by law.
                    </Typography>
                  </Card>
                  <Card className={`${classes.card} ${classes.bigCard}`}>
                    <div className={classes.cardTitle}>
                      <Typography variant="subtitle1" className={classes.cardNumber}>
                        03
                      </Typography>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Fund your account
                      </Typography>
                    </div>
                    <Divider className={classes.divider} />
                    <img
                      width={45}
                      height={45}
                      src={`/svg/funding.svg`}
                      className={classes.bigIcon}
                    />
                    <Typography variant="body1">
                      Send money to your exchange account with a bank transfer or pay by debit or
                      credit card.
                    </Typography>
                  </Card>
                  <Card className={`${classes.card} ${classes.bigCard}`}>
                    <div className={classes.cardTitle}>
                      <Typography variant="subtitle1" className={classes.cardNumber}>
                        04
                      </Typography>
                      <Typography variant="subtitle1" className={classes.purpleTitle}>
                        Buy Bitcoin
                      </Typography>
                    </div>
                    <Divider className={classes.divider} />
                    <img
                      width={45}
                      height={45}
                      src={`/svg/bitcoin.svg`}
                      className={classes.bigIcon}
                    />
                    <Typography variant="body1">
                      Choose the amount you want to buy and you’ll receive a quote. Hit the “Buy”
                      button. That’s it.
                    </Typography>
                  </Card>
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="body1">
                  There is no such thing as the perfect crypto exchange, they all have their own
                  sets of tradeoffs. Perhaps they have rigid requirements, or they block users from
                  your jurisdiction or they cater more towards professional traders and have an
                  intimidating user experience.
                </Typography>
                <br />
                <Typography variant="body1">
                  As a general rule, the more regulated the exchange is, the more protections are in
                  place to protect users. On the flip side, it also means they have more rigid
                  verification and compliance measures in place, meaning you will have to identify
                  yourself and in some cases even upload your tax-ID. There are exchanges that have
                  less verification requirements and offer more freedom, but naturally, they also
                  have less regulatory protections in place for traders.
                </Typography>
                <br />
                <Typography variant="body1">
                  We’ll be explaining all you need to know to buy your first bitcoins and find the
                  perfect exchange for you.
                </Typography>
                <br />
                <Typography variant="body1">
                  Use our <Link href={urls.exchanges}>exchange rate calculator</Link> to find the
                  best bitcoin price in real-time.
                </Typography>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Common misconceptions about bitcoin
                </Typography>
                <div className={classes.numberRow}>
                  <Typography variant="body1" className={classes.numberTitle}>
                    # 1
                  </Typography>
                  <div className={classes.rightSection}>
                    <Typography variant="subtitle1" className={classes.numberSubtitle}>
                      There is not one bitcoin price
                    </Typography>
                    <Typography variant="body1">
                      Bitcoin is traded on thousands of exchanges. The price of bitcoin varies from
                      one exchange to another and from country to country. In some countries,
                      traders pay much more for a bitcoin than in others. We’ll show you how to find
                      the best exchange rate in your country.
                    </Typography>
                  </div>
                </div>
                <br />
                <div className={classes.numberRow}>
                  <Typography variant="body1" className={classes.numberTitle}>
                    # 2
                  </Typography>
                  <div className={classes.rightSection}>
                    <Typography variant="subtitle1" className={classes.numberSubtitle}>
                      Bitcoin can’t be hacked
                    </Typography>
                    <Typography variant="body1">
                      Sometimes users hear about a hack in the cryptocurrency space and infer that
                      bitcoin is insecure. In fact, it’s never bitcoin the network that is hacked
                      but individual wallets whose private key was obtained by a hacker. These hacks
                      can be particularly painful when they affect a cryptocurrency exchange holding
                      a large number of user funds.
                    </Typography>
                  </div>
                </div>
                <br />
                <div className={classes.numberRow}>
                  <Typography variant="body1" className={classes.numberTitle}>
                    # 3
                  </Typography>
                  <div className={classes.rightSection}>
                    <Typography variant="subtitle1" className={classes.numberSubtitle}>
                      There are several options to store your bitcoins
                    </Typography>
                    <Typography variant="body1">
                      One of the big breakthroughs of bitcoin is that users can self-custody their
                      wealth in a private bitcoin wallet. Unlike a bank that can go bankrupt or
                      seize your funds, bitcoins in your wallet can not be moved by anyone but
                      yourself. If this aspect is important to you, you should withdraw your funds
                      from the cryptocurrency exchange to a bitcoin wallet after you bought them.
                    </Typography>
                  </div>
                </div>
                <br />
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Are you eligible to trade?
                </Typography>
                <Typography variant="body1">
                  This is a crucial detail to verify. Not every crypto exchange is available to
                  every user. Coinbase for instance, one of the most popular crypto exchanges, is
                  only available to a select number of countries.
                </Typography>
                <br />
                <Typography variant="body1">
                  You need to make sure you’re eligible to trade on the exchange. Most exchanges
                  have a customer verification process where you need to state which country you are
                  from. If you state that you are from a country whose citizens are not eligible to
                  trade at the exchange, the exchange won’t even let you sign up for an account.
                  Most typically, countries that are blocked include countries on US sanctions list
                  such as Iran, Venezuela etc. or countries who have crypto bans in place and the
                  exchange doesn’t want to risk offending the country. Exchanges enforce these
                  blocks in different ways by either geo-blocking IP addresses from a certain
                  country or by rejecting users in the ID verification process. This is all done to
                  prevent excessive regulatory and compliance requirements, costs, or legal
                  headaches.
                </Typography>
                <br />
                <Typography variant="body1">
                  This is all to say that you should look for a cryptocurrency exchange that caters
                  explicitly to users from your jurisdiction.
                </Typography>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Find a Bitcoin exchange in your country
                </Typography>
                <div>
                  {regions.map((region) => (
                    <Link key={region.name} href={`${urls.buyBitcoin}/${region.name}`}>
                      <Card className={classes.regionCard}>
                        <ReactCountryFlag countryCode={region.code} />
                        <Typography variant="h6" className={classes.regionName}>
                          Buy Bitcoin in {region.name}
                        </Typography>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Finding The Right Payment Method
                </Typography>
                <Typography variant="body1">
                  Another important consideration is whether the bitcoin exchange supports your
                  payment method of choice. Some exchanges do not accept any deposit with “fiat
                  currencies”, because they don’t have adequate banking relationships. This means
                  that you have to fund your account with cryptocurrency, to be able to trade on the
                  exchange. Naturally, this is not an option if you don’t already own any crypto so
                  these exchanges are not the best place to get started. We only list exchanges that
                  allow users to fund their accounts with fiat currency per bank transfer, debit
                  card, PayPal or other means of payment.
                </Typography>

                <br />

                <Typography variant="body1">
                  The reason why some exchanges don’t have banking relationships is that doing so
                  increases the regulatory burden for them. To obtain a bank partner they must deal
                  with an array of banking, financial, and tax compliance issues, which they can
                  avoid when only accepting cryptocurrencies. Only large exchanges who have the
                  resources to comply with all these regulatory tasks can offer banking services.
                </Typography>

                <br />

                <Typography variant="body1">
                  If you’re unbanked, unable, or unwilling to provide the extensive level of
                  personal information some exchanges require for verification, then your best bet
                  is to use a peer-to-peer exchange where you can buy crypto from other users like
                  on eBay.
                </Typography>
              </div>

              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  Five things you should know before you buy Bitcoin
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1">
                      <b>Bitcoin is volatile:</b> Because this is a new asset class, there is a lot
                      of price speculation going on. Historically, markets have moved very
                      drastically. If you have decided to buy bitcoin it is best not to look too
                      much at the bitcoin price as long as you believe in the fundamentals.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Bitcoin is not anonymous:</b> There’s a widespread belief that bitcoin
                      transactions are private. This is not the case as all transactions can be
                      traced back on the public ledger and tied back to a real world identity that
                      users have to submit when using a regulated exchange.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Self-custody is important:</b> One of the breakthroughs of bitcoin is that
                      users can hold their funds in non-custodial wallets, that only they can
                      control using their private key. It’s important to understand the differences
                      between holding your funds on an exchange like Coinbase or a private wallet.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Bitcoin is not slow:</b> Sometimes people praise new cryptocurrencies on
                      the grounds that they can process transactions faster than Bitcoin. It’s
                      important to understand that Bitcoin’s block size limit is a virtue and not a
                      bug. The competition between users who want to submit their transactions
                      creates a fee market which is important in order to incentivize miners to
                      secure the network.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>There are tax implications.</b> In most countries selling your bitcoin
                      constitutes a taxable event. You’ll be taxed on any capital gains you make
                      when you sell it for fiat currency. Cryptocurrency tax tools help you generate
                      reports showing your gains/losses.
                    </Typography>
                  </li>
                </ul>
              </div>
              <div className={classes.subContainer}>
                <Typography variant="h6" className={classes.mainTitle}>
                  How to Earn Bitcoin
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body1">
                      <b>Mine Bitcoin.</b> It’s now extremely hard for any individual to make money
                      from mining Bitcoin. However, if you’re still interested, learn more about
                      Bitcoin mining here.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Earn Bitcoin for shopping.</b> Providers like Fold, Lolli, and others pay
                      users rewards in bitcoin for buying gift cards from Amazon, Spotify etc or
                      shopping from a merchant belonging to their merchant network.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Get a crypto card.</b> Some crypto card providers also offer “cashback” on
                      debit card purchases. This allows you to spend fiat currency as you would
                      anyway and earn bitcoin rewards.
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography variant="body1">
                      <b>Get paid in bitcoin for services.</b> It’s increasingly common among
                      freelancers to ask to get paid in bitcoin especially for cross-border
                      transactions because it is cheaper than international bank transfers.
                    </Typography>
                  </li>
                  <br />
                </ul>
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let regions: IRegion[] = []

  try {
    const fetchedRegions = await getRegions()
    regions = fetchedRegions.filter((obj) => !!obj.guides?.length)
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      regions,
    },
  }
}

export default BuyBitcoin
